/**
 * Mode Drill — free-practice mode.
 *
 * Hear a tonic drone followed by a scale/melody in a mode.
 * Identify which of the seven church modes was played.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  appendModalContext,
  pickKey,
  MODE_PATTERNS,
  MODE_DISPLAY_NAMES,
  type Mode,
  type KeyChoice,
} from "./utils.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type { Mode as DrillModeType };

export type ModePresentationType = "ascending" | "descending" | "both" | "mixed";

export interface ModeExerciseConfig {
  /** Modes to draw from, ≥2. */
  modes: Mode[];
  presentation: ModePresentationType;
  tonic: "fixed" | "random";
  /** Only used when tonic === "fixed". */
  fixedTonicMidi: number;
  drillMode: "graded" | "listening";
  fsrsEnabled: boolean;
}

export interface ModeExerciseRound {
  mode: Mode;
  keyName: string;
  tonicMidi: number;
  effectivePresentation: Exclude<ModePresentationType, "mixed">;
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

// ─── Pool metadata ────────────────────────────────────────────────────────────

export interface ModeDrillInfo {
  /** Display name: "Major", "Minor", or the mode name. */
  displayName: string;
  /** Sublabel for the answer button (characteristic interval). */
  characteristic: string;
  group: "major-family" | "minor-family" | "other";
}

export const MODE_DRILL_POOL: Record<Mode, ModeDrillInfo> = {
  ionian:     { displayName: "Major",      characteristic: "natural major scale",    group: "major-family" },
  lydian:     { displayName: "Lydian",     characteristic: "♯4 (raised 4th)",         group: "major-family" },
  mixolydian: { displayName: "Mixolydian", characteristic: "♭7 (lowered 7th)",        group: "major-family" },
  aeolian:    { displayName: "Minor",      characteristic: "natural minor scale",    group: "minor-family" },
  dorian:     { displayName: "Dorian",     characteristic: "♮6 vs natural minor",    group: "minor-family" },
  phrygian:   { displayName: "Phrygian",   characteristic: "♭2 (lowered 2nd)",        group: "minor-family" },
  locrian:    { displayName: "Locrian",    characteristic: "♭2 + ♭5",                 group: "other" },
};

/** Canonical order for display. */
export const MODE_DRILL_ORDER: readonly Mode[] = [
  "ionian", "lydian", "mixolydian",
  "aeolian", "dorian", "phrygian",
  "locrian",
];

export interface ModeDrillGroup {
  label: string;
  modes: Mode[];
}

export const MODE_DRILL_GROUPS: readonly ModeDrillGroup[] = [
  { label: "Major family",  modes: ["ionian", "lydian", "mixolydian"] },
  { label: "Minor family",  modes: ["aeolian", "dorian", "phrygian"] },
  { label: "Other",         modes: ["locrian"] },
];

export const MODE_DRILL_PRESETS: Record<"majorFamily" | "minorFamily" | "all", Mode[]> = {
  majorFamily: ["ionian", "lydian", "mixolydian"],
  minorFamily: ["aeolian", "dorian", "phrygian"],
  all:         [...MODE_DRILL_ORDER],
};

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_MODE_EXERCISE_CONFIG: ModeExerciseConfig = {
  modes: [...MODE_DRILL_PRESETS.majorFamily],
  presentation: "ascending",
  tonic: "random",
  fixedTonicMidi: 60,
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

export function modeDrillItemId(mode: Mode): string {
  if (mode === "ionian" || mode === "lydian" || mode === "mixolydian") {
    return `modes-major-family::${mode}`;
  }
  if (mode === "dorian" || mode === "phrygian" || mode === "aeolian") {
    return `modes-minor-family::${mode}`;
  }
  return `modes-locrian::locrian`;
}

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Format: `mode-drill|${mode}|${presentation}|${tonic}`
 */
export function modeDrillStatsKey(
  mode: Mode,
  effectivePresentation: Exclude<ModePresentationType, "mixed">,
  config: Pick<ModeExerciseConfig, "tonic" | "fixedTonicMidi">,
): string {
  const tonicPart = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  return `mode-drill|${mode}|${effectivePresentation}|${tonicPart}`;
}

// ─── Fixed-key helper ─────────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedModeKey(tonicMidi: number, mode: Mode): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode,
    tonicMidi,
    name: `${letter}${accSym} ${MODE_DISPLAY_NAMES[mode]}`,
  };
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function appendScaleAscending(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  mode: Mode,
  opts: { noteDur?: number; noteGap?: number; gain?: number } = {},
): void {
  const noteDur = opts.noteDur ?? 0.5;
  const noteGap = opts.noteGap ?? 0.05;
  const gain = opts.gain ?? 0.22;
  const pattern = MODE_PATTERNS[mode];
  const base = key.tonicMidi + 12;
  for (const offset of pattern) {
    builder.addNote(base + offset, noteDur, gain, "melody");
    builder.advance(noteDur + noteGap);
  }
  builder.addNote(base + 12, noteDur * 1.6, gain, "target");
  builder.advance(noteDur * 1.6 + 0.3);
}

function appendScaleDescending(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  mode: Mode,
  opts: { noteDur?: number; noteGap?: number; gain?: number } = {},
): void {
  const noteDur = opts.noteDur ?? 0.5;
  const noteGap = opts.noteGap ?? 0.05;
  const gain = opts.gain ?? 0.22;
  const pattern = MODE_PATTERNS[mode];
  const base = key.tonicMidi + 12;
  // Upper tonic first, then pattern in reverse.
  builder.addNote(base + 12, noteDur, gain, "target");
  builder.advance(noteDur + noteGap);
  for (let i = pattern.length - 1; i >= 0; i--) {
    const isLast = i === 0;
    const dur = isLast ? noteDur * 1.6 : noteDur;
    builder.addNote(base + pattern[i]!, dur, gain, isLast ? "target" : "melody");
    builder.advance(dur + noteGap);
  }
  builder.advance(0.2);
}

/**
 * Characteristic degree index (0..6) for each mode.
 * null for ionian/aeolian — they are the reference modes.
 */
function characteristicDegreeIndex(mode: Mode): number | null {
  switch (mode) {
    case "lydian":     return 3; // ♯4
    case "mixolydian": return 6; // ♭7
    case "dorian":     return 5; // ♮6
    case "phrygian":   return 1; // ♭2
    case "locrian":    return 4; // ♭5
    default:           return null;
  }
}

function buildModeTeachAudio(key: KeyChoice, mode: Mode): AudioPlan {
  const builder = new AudioPlanBuilder();
  appendModalContext(builder, key, { droneDuration: 1.6, postRoll: 0.3 });

  const pattern = MODE_PATTERNS[mode];
  const base = key.tonicMidi + 12;
  const charIdx = characteristicDegreeIndex(mode);
  const noteDur = 0.7;
  const noteGap = 0.08;
  const gain = 0.22;

  for (let i = 0; i < pattern.length; i++) {
    const isChar = i === charIdx;
    const dur = isChar ? noteDur * 1.8 : noteDur;
    const voice = isChar ? "target" : "melody";
    builder.addNote(base + pattern[i]!, dur, gain, voice);
    builder.advance(dur + noteGap);
  }
  builder.addNote(base + 12, noteDur * 1.4, gain, "melody");
  builder.advance(noteDur * 1.4 + 0.3);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

const CONCRETE_PRESENTATIONS: ReadonlyArray<Exclude<ModePresentationType, "mixed">> = [
  "ascending", "descending", "both",
];

export function buildModeExerciseRound(
  config: ModeExerciseConfig,
  seed: number,
): ModeExerciseRound {
  const rng = new Rng(seed);

  const mode = rng.pick(config.modes as Mode[]);

  const effectivePresentation: Exclude<ModePresentationType, "mixed"> =
    config.presentation === "mixed"
      ? rng.pick(CONCRETE_PRESENTATIONS as Array<Exclude<ModePresentationType, "mixed">>)
      : config.presentation;

  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedModeKey(config.fixedTonicMidi, mode)
      : pickKey(rng, mode);

  const builder = new AudioPlanBuilder();
  appendModalContext(builder, key);

  if (effectivePresentation === "ascending") {
    appendScaleAscending(builder, key, mode);
  } else if (effectivePresentation === "descending") {
    appendScaleDescending(builder, key, mode);
  } else {
    // both: ascending then immediately descending
    appendScaleAscending(builder, key, mode);
    appendScaleDescending(builder, key, mode, { noteDur: 0.4, noteGap: 0.04 });
  }

  return {
    mode,
    keyName: key.name,
    tonicMidi: key.tonicMidi,
    effectivePresentation,
    audioPlan: builder.build(),
    teachAudio: buildModeTeachAudio(key, mode),
  };
}
